<template>
  <base-dialog
    :title="$t('inventory.adjustInventory')"
    :actionName="$t('common.action.save')"
    @action="saveInventory"
    :actionDisabled="!isValid"
    :max-width="dialogWidth"
    :showCancel="$t('common.action.cancel')"
    @close="closeDialog"
    :isLoading="isLoading"
    :show="showDialog"
  >
    <template #content>
      <div class="ma-4">
        <terminal-locations-multi-select
          v-if="locationBasedInventoryEnabled"
          v-model="locationIds"
          :allowedLocationIds="productAssignedLocationIds"
          :label="$t('page.locations.locations')"
          class="mb-4"
        />

        <v-row>
          <v-col cols="6">
            <div class="app-field-label">
              {{ $t("inventory.adjustmentType") }}
            </div>
            <v-select
              v-model="adjustmentType"
              :items="adjustmentTypes"
              item-title="label"
              item-value="value"
              class="mb-4"
            ></v-select>
          </v-col>

          <v-col cols="6">
            <div class="app-field-label">
              {{ $t("inventory.adjustmentAmount") }}
            </div>
            <v-text-field
              v-model.number="adjustmentAmount"
              type="number"
              :min="0"
              :rules="[amountRules]"
              @keypress="blockInvalidAmountCharacters"
              class="mb-4"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="app-field-label">
          {{ $t("inventory.referenceId") }}
        </div>
        <v-text-field v-model="referenceId" class="mb-4"></v-text-field>

        <div class="app-field-label">
          {{ $t("inventory.memo") }}
        </div>
        <v-textarea
          v-model="memo"
          rows="3"
          class="mb-4"
          :placeholder="$t('inventory.memoPlaceholder')"
        ></v-textarea>
      </div>
    </template>
  </base-dialog>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import BaseDialog from "@/shared/components/Common/BaseDialog.vue";
import TerminalLocationsMultiSelect from "@/shared/components/Common/TerminalLocationsMultiSelect.vue";
import i18n from "@/app/plugins/i18n";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { useProductsStore } from "@/catalog/stores/products";
import { useAccountStore } from "@/account/stores/account";
import { useLocationStore } from "@/terminals/stores/locations";
import { useDisplay } from "vuetify";
import {
  coerceLocationSelectionToAllowedLocationIds,
  normalizeLocationIds,
} from "@/terminals/utils/location.utils";
import type { AdjustInventoryRequest } from "@nectarpay/api-contracts";
import type { Product } from "@/catalog/types/products";

type AdjustmentType = AdjustInventoryRequest["adjustmentType"];

const emit = defineEmits(["updated"]);
const showDialog = ref(false);
const isLoading = ref(false);
const adjustmentAmount = ref(0);
const adjustmentType = ref<AdjustmentType>("add");
const memo = ref("");
const referenceId = ref("");
const selectedProductId = ref("");
const locationIds = ref<string[] | null>(null);
const productAssignedLocationIds = ref<string[] | null>(null);
const accountStore = useAccountStore();
const locationStore = useLocationStore();
const productsStore = useProductsStore();
const { lgAndUp, md, sm } = useDisplay();

const adjustmentTypes = [
  { label: i18n.global.t("inventory.add"), value: "add" },
  { label: i18n.global.t("inventory.remove"), value: "remove" },
  { label: i18n.global.t("inventory.setTo"), value: "set" },
];

const dialogWidth = computed(() => {
  if (lgAndUp.value) {
    return "40%";
  } else if (md.value) {
    return "50%";
  } else if (sm.value) {
    return "70%";
  } else {
    return "100%";
  }
});

// Commented this out just incase we need it again, this just helped with the Alert Modal
// const calculateNewStock = computed(() => {
//   if (adjustmentType.value === "add") {
//     return adjustmentAmount.value;
//   } else if (adjustmentType.value === "remove") {
//     return Math.max(0, adjustmentAmount.value);
//   } else {
//     return adjustmentAmount.value;
//   }
// });

const isValid = computed(() => {
  const hasLocationSelection =
    !locationBasedInventoryEnabled.value ||
    locationIds.value === null ||
    locationIds.value.length > 0;

  if (!hasLocationSelection) return false;

  if (adjustmentType.value === "set") {
    return Number.isFinite(Number(adjustmentAmount.value)) && Number(adjustmentAmount.value) >= 0;
  }
  return adjustmentAmount.value > 0;
});

const locationBasedInventoryEnabled = computed(() => {
  return (
    accountStore.account?.accountSettings?.inventory?.location_based_inventory_enabled === true
  );
});

const amountRules = (value: number) => {
  if (adjustmentType.value === "set" && !Number.isFinite(Number(value)))
    return i18n.global.t("inventory.invalidAmount");
  if (adjustmentType.value === "set" && Number(value) < 0)
    return i18n.global.t("inventory.amountMustBePositive");
  if (adjustmentType.value !== "set" && value <= 0)
    return i18n.global.t("inventory.amountMustBePositive");
  return true;
};

const blockInvalidAmountCharacters = (e: KeyboardEvent) => {
  const invalidChars = ["-", "+", "e", "E"];
  if (invalidChars.includes(e.key)) {
    e.preventDefault();
  }
};

const open = async (productIdOrObject: string | Product) => {
  showDialog.value = true;
  isLoading.value = true;

  const isProductObject = typeof productIdOrObject === "object" && productIdOrObject !== null;
  selectedProductId.value = isProductObject ? productIdOrObject.id : productIdOrObject;

  const objectHasAssignedLocations =
    isProductObject &&
    Object.prototype.hasOwnProperty.call(productIdOrObject, "assignedLocationIds");

  if (!isProductObject || !objectHasAssignedLocations) {
    await productsStore.retrieveProduct(selectedProductId.value, false);
    productAssignedLocationIds.value = normalizeLocationIds(
      productsStore.selectedProduct?.assignedLocationIds,
    );
  } else {
    productAssignedLocationIds.value = normalizeLocationIds(productIdOrObject.assignedLocationIds);
  }

  adjustmentAmount.value = 0;
  adjustmentType.value = "add";
  memo.value = "";
  referenceId.value = "";
  const initialLocationIds = locationBasedInventoryEnabled.value
    ? locationStore.selectedLocation?.id
      ? [locationStore.selectedLocation.id]
      : null
    : null;

  locationIds.value = coerceLocationSelectionToAllowedLocationIds(
    initialLocationIds,
    productAssignedLocationIds.value,
  );

  isLoading.value = false;
};

const saveInventory = async () => {
  isLoading.value = true;

  try {
    const payload: AdjustInventoryRequest = {
      adjustmentType: adjustmentType.value,
      amount: adjustmentAmount.value,
      referenceNumber: referenceId.value,
      memo: memo.value,
    };

    if (locationBasedInventoryEnabled.value) {
      // null explicitly means all locations. When a product is assigned to a subset, "all"
      // means all assigned locations rather than every location in the account.
      payload.locationIds =
        locationIds.value === null && Array.isArray(productAssignedLocationIds.value)
          ? productAssignedLocationIds.value
          : locationIds.value;
    }

    await useProductsStore().adjustInventory(selectedProductId.value, payload);

    useSnackBarStore().toastSuccess({
      message: i18n.global.t("inventory.adjustmentSuccessful"),
    });
    closeDialog();
    emit("updated");
  } catch (error) {
    console.error("Adjustment failed:", error);
    useSnackBarStore().toastError({
      message: i18n.global.t("inventory.adjustmentFailed"),
    });
  }

  isLoading.value = false;
};

const closeDialog = () => {
  showDialog.value = false;
};

defineExpose({
  open,
});
</script>
